import { useI18n } from '@superbet-group/web.lib.vue-utilities';

import { SportId } from '@core/constants';

export function getPeriodNames(sportId: SportId, numberOfPeriods: number, useCompactView = false): string[] {
    const { translate } = useI18n();
    return getPeriodNamesWithTranslations(translate, sportId, numberOfPeriods, useCompactView);
}

export function getPeriodNamesWithTranslations(
    t: translateFunction,
    sportId: SportId,
    numberOfPeriods: number,
    useCompactView = false,
): string[] {
    if (sportId in periodsNameGetterMapping) {
        return periodsNameGetterMapping[sportId]!(t, numberOfPeriods, useCompactView);
    } else {
        return [];
    }
}

const getFootballPeriodNames: periodNameGetter = (t, numberOfPeriods) => {
    const footballPeriods: string[] = [];
    if (numberOfPeriods > 2) {
        footballPeriods.push('sportOffer.periods.halftime', 'sportOffer.periods.fulltime');
    } else {
        footballPeriods.push('sportOffer.periods.first_half', 'sportOffer.periods.second_half');
    }
    footballPeriods.push('sportOffer.periods.extra_time', 'sportOffer.periods.penalties');
    // in second half show only label for first half
    return footballPeriods.slice(0, numberOfPeriods === 2 ? 1 : numberOfPeriods).map((p) => t(p));
};

const getTennisPeriodNames: periodNameGetter = (t, numberOfPeriods, useCompactView) => {
    if (useCompactView) {
        return [
            'sportOffer.score_widget.points',
            'sportOffer.periods.set',
            'sportOffer.score_widget.match',
        ].map((key) => t(key));
    } else {
        return getDefaultServeSportPeriodNames(t, numberOfPeriods, true);
    }
};

const getServeSportPeriodNames: periodNameGetter = (t, numberOfPeriods) => {
    return getDefaultServeSportPeriodNames(t, numberOfPeriods);
};

const getBasketballPeriodNames: periodNameGetter = (t, numberOfPeriods) => {
    let numberOfPeriodsWithoutFinalScore = numberOfPeriods;
    // if there is only one period final score is equal to score in that period
    // so for higher number of periods we want to subtract final score
    if (numberOfPeriods > 1) {
        numberOfPeriodsWithoutFinalScore = numberOfPeriods - 1;
    }
    const periods: string[] = [];
    for (let i = 0; i < numberOfPeriodsWithoutFinalScore && i < 4; i++) {
        periods.push(`${t('sportOffer.periods.quarter')}${i + 1}`);
    }
    if (numberOfPeriodsWithoutFinalScore > 4) {
        for (let i = 0; i < numberOfPeriodsWithoutFinalScore - 4; i++) {
            periods.push(`${t('sportOffer.periods.overtime')}${i + 1}`);
        }
    }
    return periods;
};

const getHandballPeriodNames: periodNameGetter = (t, numberOfPeriods) => {
    let numberOfPeriodsWithoutFinalScore = numberOfPeriods;
    // if there is only one period final score is equal to score in that period
    // so for higher number of periods we want to subtract final score
    if (numberOfPeriods > 1) {
        numberOfPeriodsWithoutFinalScore = numberOfPeriods - 1;
    }
    const overtimeTranslation = t('sportOffer.periods.overtime');
    const handballPeriods = [
        t('sportOffer.periods.first_half'),
        t('sportOffer.periods.second_half'),
        `${overtimeTranslation}1`,
        `${overtimeTranslation}2`,
        t('sportOffer.periods.penalties'),
    ];
    return handballPeriods.slice(0, numberOfPeriodsWithoutFinalScore);
};

const getRugbyPeriodNames: periodNameGetter = (t, numberOfPeriods) => {
    let numberOfPeriodsWithoutFinalScore = numberOfPeriods;
    // if there is only one period final score is equal to score in that period
    // so for higher number of periods we want to subtract final score
    if (numberOfPeriods > 1) {
        numberOfPeriodsWithoutFinalScore = numberOfPeriods - 1;
    }
    const handballPeriods = [
        'sportOffer.periods.first_half',
        'sportOffer.periods.second_half',
        'sportOffer.periods.extra_time',
        'sportOffer.periods.overtime',
        'sportOffer.periods.penalties',
    ];
    return handballPeriods.slice(0, numberOfPeriodsWithoutFinalScore).map((p) => t(p));
};

const getIceHockeyPeriodNames: periodNameGetter = (t, numberOfPeriods) => {
    let numberOfPeriodsWithoutFinalScore = numberOfPeriods;
    // if there is only one period final score is equal to score in that period
    // so for higher number of periods we want to subtract final score
    if (numberOfPeriods > 1) {
        numberOfPeriodsWithoutFinalScore = numberOfPeriods - 1;
    }
    const periods: string[] = [];
    for (let i = 0; i < numberOfPeriodsWithoutFinalScore && i < 3; i++) {
        periods.push(`${t('sportOffer.periods.period')}${i + 1}`);
    }
    const extraHockeyPeriods = [
        'sportOffer.periods.overtime',
        'sportOffer.periods.penalties',
    ];
    if (numberOfPeriodsWithoutFinalScore > 3) {
        for (let i = 0; i < numberOfPeriodsWithoutFinalScore - 3; i++) {
            periods.push(`${t(extraHockeyPeriods[i])}`);
        }
    }
    return periods;
};

function getDefaultServeSportPeriodNames(
    t: translateFunction,
    numberOfPeriods: number,
    includeCurrentPoints = false,
): string[] {
    const periods: string[] = [];
    let finalNumberOfPeriods = numberOfPeriods;
    if (includeCurrentPoints) {
        periods.push(t('sportOffer.score_widget.points'));
        // points is included in periods so there is one period less
        finalNumberOfPeriods = finalNumberOfPeriods - 1;
    }
    for (let i = 0; i < finalNumberOfPeriods; i++) {
        periods.push(`${t('sportOffer.periods.set')}${i + 1}`);
    }
    return periods;
}

const periodsNameGetterMapping: Partial<Record<SportId, periodNameGetter>> = {
    [SportId.Tennis]: getTennisPeriodNames,
    [SportId.Football]: getFootballPeriodNames,
    [SportId.Volleyball]: getServeSportPeriodNames,
    [SportId.TableTennis]: getServeSportPeriodNames,
    [SportId.BeachVolleyball]: getServeSportPeriodNames,
    [SportId.Badminton]: getServeSportPeriodNames,
    [SportId.Basketball]: getBasketballPeriodNames,
    [SportId.Handball]: getHandballPeriodNames,
    [SportId.IceHockey]: getIceHockeyPeriodNames,
    [SportId.Rugby]: getRugbyPeriodNames,
};

type translateFunction = (message: string) => string;
type periodNameGetter = (t: translateFunction, numberOfPeriods: number, useCompactView: boolean) => string[];
